<script lang="ts">
export default {
  name: 'Tabs',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { TabsRoot, TabsList, TabsTrigger, TabsContent } from 'reka-ui';

export interface TabItem {
  value: string;
  label: string;
  icon?: any;
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    items: TabItem[];
    variant?: 'bordered' | 'lifted' | 'boxed';
    class?: string;
  }>(),
  {
    variant: 'bordered',
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const currentTab = computed({
  get: () => props.modelValue || props.items[0]?.value || '',
  set: (value) => emit('update:modelValue', value),
});

const tabsRootClasses = computed(() => {
  const classes = ['tw:w-full'];
  
  if (props.class) {
    classes.push(props.class);
  }
  
  return classes.join(' ');
});

const tabsListClasses = computed(() => {
  const classes = ['tw:tabs', 'tw:flex'];
  
  switch (props.variant) {
    case 'lifted':
      classes.push('tw:tabs-lift');
      break;
    case 'boxed':
      classes.push('tw:tabs-box');
      break;
    case 'bordered':
    default:
      classes.push('tw:tabs-border');
      break;
  }
  
  return classes.join(' ');
});
</script>

<template>
  <TabsRoot v-model="currentTab" :class="tabsRootClasses">
    <TabsList :class="tabsListClasses">
      <TabsTrigger
        v-for="item in items"
        :key="item.value"
        :value="item.value"
        :disabled="item.disabled"
        class="tw:tab"
        :class="{ 'tw:tab-active': currentTab === item.value }"
      >
        <component v-if="item.icon" :is="item.icon" class="tw:w-4 tw:h-4 tw:mr-2" />
        {{ item.label }}
      </TabsTrigger>
    </TabsList>
    
    <TabsContent
      v-for="item in items"
      :key="`content-${item.value}`"
      :value="item.value"
      class="tw:mt-4"
    >
      <slot :name="item.value">
        <slot name="default" :item="item" />
      </slot>
    </TabsContent>
  </TabsRoot>
</template>
